// Sweden VAT profile (issue #287) — the deterministic rule.
// Two layers: (1) treatment / place-of-supply is decided purely from structured
// facts (no LLM); (2) the rate comes from a maintained category→rate table, where
// the category is assigned upstream by the LLM article classifier (or, as a
// fallback here, a keyword stand-in). The user never picks VAT — the result is
// surfaced with a reason. Nordic/EU expansion = another profile, not a rewrite.
//
// SINGLE SOURCE OF TRUTH (issue #287 follow-up): this file used to be duplicated
// three ways — here, in src/services/invoiceGeneration/vat.ts (consultant app),
// and in apps/client/lib/invoiceGeneration/vat.ts — and a real Skatteverket rate
// change (the temporary food-VAT cut below) had to be hand-applied to all three
// copies to avoid the live invoice preview silently disagreeing with what the
// backend actually charges. Both frontends now import CATEGORY_RATE/EU_COUNTRY_CODES/
// rateForCategory/resolveVatTreatment straight from this package
// (@financialharmony/tax-se) instead of keeping their own copies; only presentation
// text (English vs Swedish reason strings, a preview-only fallback for an EU buyer
// who hasn't entered a VAT number yet) stays app-specific. The backend
// (supabase/functions/_shared/tax/vat/index.ts) imports this exact file by
// relative path — see that file's header comment.

import type { Language, ResolveVatInput, VatResolution, VatTreatment } from "./types.ts";

// Exported so both frontend previews (which decide "SE" / "EU" / "non_EU" for
// display/country-picker purposes) can classify a country without keeping a
// second copy of the EU member list.
export const EU_COUNTRY_CODES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

// Output VAT (utgående moms) BAS account per rate — sale side. Confirmed against
// this repo's seeded chart_of_accounts (chart_type='BAS_2025'), not guessed.
const OUTPUT_VAT_ACCOUNT: Record<number, string> = { 25: "2611", 12: "2621", 6: "2631" };
// Input VAT (ingående moms) — purchase side.
const INPUT_VAT_ACCOUNT = "2641";
// Domestic revenue accounts per rate, same confirmed source. 3004 = momsfri (domestic
// exempt only, e.g. healthcare/education/financial). EU/export sales use DIFFERENT BAS
// revenue accounts (e.g. 3305/3308/3395) that are NOT yet confirmed present in this
// repo's chart_of_accounts seed — deliberately left unset below rather than guessed,
// since createJournalEntry() hard-throws ACCOUNT_NOT_FOUND on an unseeded number. The
// deterministic sales-entry generator (InvoiceGenerationService) must fall back to the
// existing LLM bookkeeping-suggestion path for eu_reverse_charge/export invoices until
// those accounts are confirmed and added here.
const DOMESTIC_REVENUE_ACCOUNT: Record<number, string> = { 25: "3001", 12: "3002", 6: "3003" };
const DOMESTIC_EXEMPT_REVENUE_ACCOUNT = "3004";

/**
 * Per-rate BAS account lookups, exported so a multi-rate invoice's deterministic
 * sales-entry generator (InvoiceGenerationService) can resolve each VAT group's
 * accounts independently — a single `resolveVatTreatment()` call only returns ONE
 * revenueAccount/outputVatAccount for the invoice's overall treatment, which is
 * correct for the treatment decision (domestic vs cross-border) but NOT enough for
 * an invoice mixing rates (e.g. a 25% consulting line + a 6% book line).
 */
export function domesticOutputVatAccountForRate(rate: number): string | undefined {
  return OUTPUT_VAT_ACCOUNT[rate];
}
export function domesticRevenueAccountForRate(rate: number): string | undefined {
  return rate > 0 ? DOMESTIC_REVENUE_ACCOUNT[rate] : DOMESTIC_EXEMPT_REVENUE_ACCOUNT;
}

/**
 * Every domestic VAT rate this system can actually BOOK, derived from the account maps
 * above rather than written out again — `salesEntryBuilder` hard-throws for any rate with
 * no confirmed BAS revenue account, so a hand-maintained list here could drift into
 * offering a user a rate that blocks their invoice at mark-as-sent.
 *
 * 0 is included deliberately and is not a key of DOMESTIC_REVENUE_ACCOUNT: momsfri maps
 * to its own account (3004) via the `rate > 0` branch above.
 *
 * Descending, because that is the order a rate picker should offer them in (standard
 * first, exempt last).
 */
export const DOMESTIC_VAT_RATES: readonly number[] = Object.freeze(
  [...Object.keys(DOMESTIC_REVENUE_ACCOUNT).map(Number), 0].sort((a, b) => b - a),
);

// The maintained reduced/exempt table. The LLM assigns the category key; the rate
// is authoritative here. Standard is 25% (the default with no category).
//
// reduced_food is TEMPORARILY 6% (normally 12%): Riksdagen cut VAT on food/groceries
// (incl. takeaway) from 12% to 6% for 2026-04-01 through 2027-12-31 inclusive, per
// Skatteverket's own announcement (skatteverket.se, "Livsmedelsmomsen sänks till 6
// procent") — confirmed 2026-08-09. Alcohol (spirits/wine/strong beer) is EXCLUDED
// from the cut and stays 25% regardless of channel (see classifyVatCategory's prompt,
// which must route it to `standard`, not `reduced_food`).
// !!! REVERT reduced_food TO 12 ON 2028-01-01 — the temporary law expires
// 2027-12-31. Deliberately a manual/reviewed rate table, not date-driven logic:
// don't build automatic date-switching for this, just don't forget the revert.
export const CATEGORY_RATE: Record<string, number> = {
  standard: 25,
  reduced_food: 6, // TEMP 6% through 2027-12-31 — see comment above. Normally 12.
  reduced_hotel: 12, // hotel/other accommodation AND camping-site rental (same rate).
  reduced_restaurant: 12, // dine-in / table-service restaurant & catering.
  reduced_repair: 12, // repair of bicycles, shoes, clothing, leather goods.
  reduced_art: 12, // artwork sold by the artist or a gallery, per Skatteverket's
  // unified 12% rate for all taxable artwork sales (2025-01-01 rule). NOT modeled:
  // an artist selling their OWN work can be VAT-free below a ~300k SEK/year
  // threshold (retroactively liable for 12% on the whole year if exceeded). That
  // needs cumulative annual-revenue tracking this app doesn't do; defaulting to
  // 12% is the simpler, more common case (most invoicing sellers are either above
  // the threshold or already VAT-registered generally) — a documented gap, not a
  // silent guess. A seller who genuinely qualifies for the sub-threshold exemption
  // needs manual handling outside this engine for now.
  reduced_books: 6,
  reduced_culture: 6, // concerts, theatre, opera, ballet admission; copyright transfer
  // in an artistic work. Deliberately NOT cinema/biograf admission: VAT on cinema
  // tickets was raised from 6% to 25% effective 2017-01-01 (Prop. 2016/17:1,
  // confirmed by Riksdagens skatteutskott, betänkande 2016/17:SkU25) and has stayed
  // 25% since — a 2026 Filmutredningen proposal to lower it back to 6% exists but is
  // NOT enacted law (confirmed against skatteverket.se and riksdagen.se 2026-08-09).
  // Route cinema admission to `standard`, not here.
  exempt_museum: 0, // museum admission (also: guide fees, travelling-exhibition
  // compensation) run by, OR continuously (multi-year) supported to more than an
  // insignificant extent by, the state, a region, or a municipality ("det allmänna").
  // Skatteverket's own bright line for "more than insignificant": public support
  // regularly at or above ~10% of the museum's total revenue (SKV 562 "Moms inom
  // kulturområdet", "Museiverksamhet" — confirmed 2026-08-09). A privately-run,
  // NOT publicly-supported museum stays reduced_culture at 6% — this app's LLM
  // classifier can only see a line-item description, so the prompt is written to
  // default to reduced_culture unless the description clearly signals public
  // operation/funding (statligt/kommunalt/regionalt museum, kommunalt stöd, etc.).
  reduced_sports: 6, // sports/fitness activity access (gym, träning, padel/tennis courts).
  reduced_transport: 6, // PASSENGER transport only (train/bus/flight/taxi within
  // Sweden). Freight/cargo transport is standard 25% — never this category.
  reduced_skilift: 12, // Ski-lift passes are their OWN 12% category — NOT the 6%
  // reduced_transport or reduced_sports rate, despite superficially looking like
  // either. When passenger-transport VAT dropped to 6% in 2000/01, ski lifts were
  // deliberately left at 12%; Skatteverket successfully defended that split at the
  // Supreme Administrative Court against a sports-facility (6%) argument (RÅ
  // 2006:11) — confirmed still the current position 2026-08-09. A narrow but
  // real category for this app's ski-resort/mountain-tourism SME users.
  exempt_healthcare: 0, exempt_education: 0, exempt_financial: 0,

  // --- Detected-and-blocked categories (#287 follow-up) — NOT full VAT support. ---
  // The LLM classifier may return either of these when a line-item description
  // sounds like one of these cases, but `LlmService.classifyVatCategory` THROWS
  // immediately when it sees one (see UNSUPPORTED_CATEGORIES below) rather than
  // letting a caller compute/persist a rate for it — so no caller of
  // rateForCategory()/resolveVatTreatment() ever actually sees one of these two
  // categories in practice. The rate below is a defensive placeholder only, kept
  // so CATEGORY_RATE/VAT_CATEGORY_KEYS stay total functions (e.g. for a zod schema
  // that must recognize the key as valid) — it is never the value a real invoice
  // line ends up charged.
  //
  // unsupported_margin_scheme: resale of used/second-hand goods, antiques, or
  // art/collectibles by a dealer who isn't the original creator (used-car dealer,
  // second-hand shop, antique dealer, pawnshop resale). Under vinstmarginal-
  // beskattning (VMB), VAT is computed on the seller's MARGIN (sale price minus
  // acquisition cost) — this app's line items have no acquisition-cost field, and
  // VMB additionally forbids showing VAT as a separate invoice line (skatteverket.se,
  // "Vinstmarginalbeskattning av begagnade varor med mera", confirmed 2026-08-09) —
  // a fundamentally different invoice, not just a different rate. Full support
  // needs: an acquisition-cost field on the line item, margin-based VAT computation,
  // and VMB-specific invoice wording/no-VAT-line rendering — out of scope here.
  unsupported_margin_scheme: 25,
  //
  // unsupported_property_rental: rental of COMMERCIAL premises (lokal) — office,
  // retail, warehouse, industrial, or other business space let to a business tenant
  // for their own taxable activity. Property rental is exempt by default, but a
  // landlord can voluntarily opt a SPECIFIC property into VAT liability (frivillig
  // skattskyldighet, 25% — skatteverket.se "Uthyrning av fastighet och frivillig
  // beskattning", confirmed 2026-08-09); this app has no per-property opt-in data,
  // so it can't safely default to either exempt or 25% from a description alone.
  // Previously this fell through to `standard` (25%) by default via the unrecognized-
  // category fallback below — usually WRONG, since most commercial rent is either
  // exempt or depends on a fact this app doesn't have; now explicitly blocked
  // instead of silently guessed. Does NOT cover residential/private housing rental
  // (bostad, lägenhet) — always exempt, no opt-in possible — or short-term paid
  // accommodation (reduced_hotel).
  unsupported_property_rental: 25,
};
const EXEMPT_CATEGORIES = new Set(["exempt_healthcare", "exempt_education", "exempt_financial", "exempt_museum"]);

// Categories the LLM classifier may return that this app deliberately does NOT
// compute VAT for (see the CATEGORY_RATE comments above for why each is
// unsupported). Exported so `LlmService.classifyVatCategory` (the only caller
// that ever sees a freshly-classified category, before it's persisted anywhere)
// can throw a clear, specific error instead of letting either value reach
// rateForCategory()/resolveVatTreatment() and silently compute a wrong VAT
// figure for a case this engine fundamentally can't handle yet.
export const UNSUPPORTED_CATEGORIES = new Set(["unsupported_margin_scheme", "unsupported_property_rental"]);

/**
 * Rate for a classified category — used to give a new article's `default_vat_rate`
 * a real, category-derived value at creation time (see `ArticleService.findOrCreate`)
 * instead of hardcoding 25 there and drifting from this table.
 */
export function rateForCategory(category: string | null | undefined): number {
  return category ? CATEGORY_RATE[category] ?? 25 : 25;
}

// Exported so the LLM article classifier (LlmService.classifyVatCategory) can
// validate its own output against the single, authoritative category table here
// rather than maintaining a second hardcoded copy of these keys.
export const VAT_CATEGORY_KEYS = new Set(Object.keys(CATEGORY_RATE));

// Keyword stand-in for the LLM classifier — used only when no vatCategory is stored.
// Hotel/restaurant are checked ahead of the generic food terms: reduced_food dropped
// to 6% (see CATEGORY_RATE) while reduced_hotel/reduced_restaurant stayed at 12%, so
// conflating them here would silently undercharge a dine-in restaurant/hotel line.
function classify(description?: string | null): string | undefined {
  if (!description) return undefined;
  const d = description.toLowerCase();
  if (/(bok|böck|fotobok|tidning|e-bok)/.test(d)) return "reduced_books";
  if (/(konsert|biljett|teater|museum|kultur)/.test(d)) return "reduced_culture";
  if (/(hotell|vandrarhem|pensionat|camping)/.test(d)) return "reduced_hotel";
  if (/(restaurang|catering)/.test(d)) return "reduced_restaurant";
  if (/(mat|livsmedel|lunch|frukost)/.test(d)) return "reduced_food";
  return undefined;
}

const isEU = (cc: string) => EU_COUNTRY_CODES.has(cc.toUpperCase());

/**
 * The statutory wording a treatment requires on the invoice (ML 17 kap.); none for domestic VAT.
 * FIN-1580: per invoice document_language, not the UI language — the English `eu_reverse_charge`
 * and `export` sentences are the document-facing wording agreed in specs/invoice-document-i18n/
 * requirements.md, not a literal translation of the short Swedish phrase.
 */
const REQUIRED_WORDING: Partial<Record<VatTreatment, Record<Language, string>>> = {
  exempt: {
    sv: "Ej momsregistrerad",
    en: "Not registered for VAT",
  },
  eu_reverse_charge: {
    sv: "Omvänd skattskyldighet – köparen ska redovisa mervärdesskatten.",
    en: "Reverse charge – VAT to be accounted for by the recipient.",
  },
  export: {
    sv: "Export, utanför EU",
    en: "Export, outside the EU",
  },
};

/** The wording for an already-decided treatment, e.g. one stored on an issued invoice. */
export function requiredWordingForTreatment(treatment: string | null | undefined, language: Language = "sv"): string | null {
  return (treatment && REQUIRED_WORDING[treatment as VatTreatment]?.[language]) || null;
}

/**
 * Cross-border place-of-supply (deterministic), or `null` for a buyer in the seller's own
 * country or an EU private consumer (v1; OSS out of scope) — both fall through to domestic.
 * Split out of resolveVatTreatment to keep its cognitive complexity within sonarjs's limit.
 */
function resolveCrossBorderTreatment(
  buyer: ResolveVatInput["buyer"],
  buyerCountry: string,
  language: Language,
): VatResolution | null {
  if (isEU(buyerCountry) && buyer.entityType !== "individual") {
    // EU B2B → reverse charge. VAT number is required to issue.
    return {
      treatment: "eu_reverse_charge", rate: 0,
      requiredWording: REQUIRED_WORDING.eu_reverse_charge?.[language],
      requiredBuyerFields: buyer.vatNumber ? undefined : ["vatNumber"],
      reason: buyer.vatNumber
        ? "EU B2B – köparen redovisar momsen"
        : "EU B2B – köparens VAT-nr krävs för omvänd skattskyldighet",
      confidence: "certain",
    };
  }
  if (!isEU(buyerCountry)) {
    return {
      treatment: "export", rate: 0, requiredWording: REQUIRED_WORDING.export?.[language],
      reason: "Export utanför EU", confidence: "certain",
    };
  }
  return null;
}

/**
 * Domestic rate: stored category (LLM) → table; else keyword; else standard 25%. Split out of
 * resolveVatTreatment to keep its cognitive complexity within sonarjs's limit.
 */
function resolveDomesticTreatment(direction: ResolveVatInput["direction"], supply: ResolveVatInput["supply"]): VatResolution {
  const category = supply.vatCategory ?? classify(supply.description);
  let rate = 25;
  let confidence: VatResolution["confidence"] = "certain";
  let reason = "Standardmoms 25%";
  if (category && category in CATEGORY_RATE) {
    rate = CATEGORY_RATE[category];
    confidence = supply.vatCategory ? "certain" : "assumed";
    reason = EXEMPT_CATEGORIES.has(category) ? `Momsfri (${category})` : `Reducerad moms ${rate}% (${category})`;
  }

  const isExempt = rate === 0 && !!category && EXEMPT_CATEGORIES.has(category);
  const result: VatResolution = {
    treatment: isExempt ? "exempt" : "domestic",
    rate, reason, confidence,
  };
  if (direction === "sale" && rate > 0) result.outputVatAccount = OUTPUT_VAT_ACCOUNT[rate];
  if (direction === "purchase" && rate > 0) result.inputVatAccount = INPUT_VAT_ACCOUNT;
  if (direction === "sale") {
    // Domestic revenue account is confirmed (3001/3002/3003 per rate, 3004 exempt).
    // Only reachable here since the cross-border branches above already returned.
    result.revenueAccount = rate > 0 ? DOMESTIC_REVENUE_ACCOUNT[rate] : DOMESTIC_EXEMPT_REVENUE_ACCOUNT;
  }
  return result;
}

export function resolveVatTreatment(input: ResolveVatInput, language: Language = "sv"): VatResolution {
  const { direction, seller, buyer, supply } = input;
  const sellerCountry = (seller.countryCode || "SE").toUpperCase();
  const buyerCountry = (buyer.countryCode || "SE").toUpperCase();

  // Seller below the VAT threshold (momsbefriad) — no VAT at all.
  if (direction === "sale" && seller.vatRegistered === false) {
    return {
      treatment: "exempt", rate: 0, requiredWording: REQUIRED_WORDING.exempt?.[language],
      reason: "Säljaren är inte momsregistrerad", confidence: "certain",
      revenueAccount: DOMESTIC_EXEMPT_REVENUE_ACCOUNT,
    };
  }

  // Cross-border place-of-supply. Only for sales in v1.
  if (direction === "sale" && buyerCountry !== sellerCountry) {
    const crossBorder = resolveCrossBorderTreatment(buyer, buyerCountry, language);
    if (crossBorder) return crossBorder;
    // EU private consumer → Swedish VAT (v1; OSS is out of scope). Falls through.
  }

  return resolveDomesticTreatment(direction, supply);
}
